// src/modules/Admin/pages/Jobs/index.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { supabase } from '../../../../config/supabase';
import {
  FaPlus,
  FaSearch,
  FaEdit,
  FaTrash,
  FaEye,
  FaEllipsisV,
  FaBriefcase,
  FaPause,
  FaPlay,
  FaArchive,
  FaUsers,
  FaExternalLinkAlt,
  FaCopy,
  FaFileAlt
} from 'react-icons/fa';

// Styled Components
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #111;
`;

const CreateButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: #111;
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: background 200ms;

  &:hover {
    background: #333;
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

  .stat-label {
    font-size: 13px;
    color: #666;
    margin-bottom: 4px;
  }

  .stat-value {
    font-size: 28px;
    font-weight: 700;
    color: #111;
  }

  .stat-change {
    font-size: 12px;
    color: ${props => props.$positive ? '#10b981' : '#999'};
    margin-top: 4px;
  }
`;

const FiltersBar = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const SearchInput = styled.div`
  position: relative;
  flex: 1;
  min-width: 200px;
  max-width: 400px;

  input {
    width: 100%;
    padding: 12px 16px 12px 44px;
    border: 1px solid #e5e5e5;
    border-radius: 8px;
    font-size: 14px;
    transition: all 200ms;

    &:focus {
      outline: none;
      border-color: #333;
      box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
    }
  }

  svg {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #999;
    font-size: 16px;
  }
`;

const FilterSelect = styled.select`
  padding: 12px 16px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  min-width: 150px;

  &:focus {
    outline: none;
    border-color: #333;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 16px 20px;
  font-size: 12px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
`;

const Td = styled.td`
  padding: 16px 20px;
  border-bottom: 1px solid #f5f5f5;
  vertical-align: middle;
`;

const JobInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const JobTitle = styled.div`
  font-weight: 600;
  color: #111;
  font-size: 14px;
`;

const JobDepartment = styled.div`
  font-size: 13px;
  color: #666;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;

  ${props => {
    switch (props.$status) {
      case 'active':
        return `background: #d1fae5; color: #065f46;`;
      case 'paused':
        return `background: #fef3c7; color: #92400e;`;
      case 'closed':
        return `background: #f3f4f6; color: #6b7280;`;
      default:
        return `background: #f3f4f6; color: #6b7280;`;
    }
  }}
`;

const MetaInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 13px;
  color: #666;
`;

const Actions = styled.div`
  position: relative;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  color: #666;
  transition: all 200ms;

  &:hover {
    background: #f5f5f5;
    color: #111;
  }
`;

const ActionMenu = styled.div`
  position: absolute;
  right: 0;
  top: 100%;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  min-width: 180px;
  z-index: 10;
  overflow: hidden;
`;

const ActionItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: transparent;
  font-size: 13px;
  color: ${props => props.$danger ? '#dc2626' : '#333'};
  cursor: pointer;
  text-align: left;
  transition: background 200ms;

  &:hover {
    background: ${props => props.$danger ? '#fef2f2' : '#f5f5f5'};
  }

  svg {
    font-size: 14px;
    color: ${props => props.$danger ? '#dc2626' : '#666'};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;

  svg {
    font-size: 48px;
    color: #ddd;
    margin-bottom: 16px;
  }

  h3 {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
  }

  p {
    font-size: 14px;
    color: #666;
    margin-bottom: 20px;
  }
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: ${props => props.$new ? '#dbeafe' : '#f3f4f6'};
  color: ${props => props.$new ? '#1e40af' : '#6b7280'};
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;

  svg {
    font-size: 10px;
  }
`;

const Toast = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  padding: 16px 24px;
  background: ${props => props.$type === 'error' ? '#dc2626' : '#111'};
  color: white;
  border-radius: 8px;
  font-size: 14px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  z-index: 100;
  animation: slideIn 300ms ease;

  @keyframes slideIn {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const Jobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [openMenu, setOpenMenu] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    const handleClick = () => setOpenMenu(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const fetchJobs = async () => {
    try {
      const { data, error } = await supabase
        .from('jobs')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setJobs(data || []);
    } catch (error) {
      console.error('Erro ao buscar vagas:', error);
      showToast('Erro ao carregar vagas', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const { error } = await supabase
        .from('jobs')
        .update({
          status: newStatus,
          ...(newStatus === 'active' ? { published_at: new Date().toISOString() } : {})
        })
        .eq('id', id);

      if (error) throw error;

      setJobs(jobs.map(job =>
        job.id === id ? { ...job, status: newStatus } : job
      ));

      const statusMessages = {
        'active': 'Vaga publicada com sucesso',
        'paused': 'Vaga pausada',
        'closed': 'Vaga encerrada'
      };
      showToast(statusMessages[newStatus]);
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      showToast('Erro ao atualizar status', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta vaga? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('jobs')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setJobs(jobs.filter(job => job.id !== id));
      showToast('Vaga excluída com sucesso');
    } catch (error) {
      console.error('Erro ao excluir vaga:', error);
      showToast('Erro ao excluir vaga', 'error');
    }
  };

  const copyShareUrl = (slug) => {
    const url = `https://www.vegarobotics.com.br/carreiras/${slug}`;
    navigator.clipboard.writeText(url);
    showToast('Link copiado para a área de transferência');
  };

  // Filtros
  const departments = [...new Set(jobs.map(job => job.department))];

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.department.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || job.status === filterStatus;
    const matchesDepartment = filterDepartment === 'all' || job.department === filterDepartment;
    return matchesSearch && matchesStatus && matchesDepartment;
  });

  // Stats
  const activeJobs = jobs.filter(j => j.status === 'active').length;
  const totalApplications = jobs.reduce((sum, j) => sum + (j.applications_count || 0), 0);
  const totalViews = jobs.reduce((sum, j) => sum + (j.views || 0), 0);

  const getStatusLabel = (status) => {
    const labels = {
      'active': 'Ativa',
      'paused': 'Pausada',
      'closed': 'Encerrada'
    };
    return labels[status] || status;
  };

  const getTypeLabel = (type) => {
    const labels = {
      'full-time': 'Tempo Integral',
      'part-time': 'Meio Período',
      'contract': 'Contrato',
      'internship': 'Estágio',
      'freelance': 'Freelance'
    };
    return labels[type] || type;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <Container>
      <Header>
        <Title>Vagas</Title>
        <CreateButton to="/admin/vagas/nova">
          <FaPlus />
          Nova Vaga
        </CreateButton>
      </Header>

      <StatsRow>
        <StatCard>
          <div className="stat-label">Total de Vagas</div>
          <div className="stat-value">{jobs.length}</div>
        </StatCard>
        <StatCard $positive>
          <div className="stat-label">Vagas Ativas</div>
          <div className="stat-value">{activeJobs}</div>
        </StatCard>
        <StatCard>
          <div className="stat-label">Total de Candidaturas</div>
          <div className="stat-value">{totalApplications}</div>
        </StatCard>
        <StatCard>
          <div className="stat-label">Visualizações</div>
          <div className="stat-value">{totalViews}</div>
        </StatCard>
      </StatsRow>

      <FiltersBar>
        <SearchInput>
          <FaSearch />
          <input
            type="text"
            placeholder="Buscar vagas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchInput>

        <FilterSelect
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="all">Todos os status</option>
          <option value="active">Ativas</option>
          <option value="paused">Pausadas</option>
          <option value="closed">Encerradas</option>
        </FilterSelect>

        <FilterSelect
          value={filterDepartment}
          onChange={(e) => setFilterDepartment(e.target.value)}
        >
          <option value="all">Todos os departamentos</option>
          {departments.map(dept => (
            <option key={dept} value={dept}>{dept}</option>
          ))}
        </FilterSelect>
      </FiltersBar>

      <Card>
        {loading ? (
          <EmptyState>
            <p>Carregando vagas...</p>
          </EmptyState>
        ) : filteredJobs.length === 0 ? (
          <EmptyState>
            <FaBriefcase />
            <h3>Nenhuma vaga encontrada</h3>
            <p>
              {jobs.length === 0
                ? 'Crie sua primeira vaga para começar a receber candidaturas.'
                : 'Tente ajustar os filtros de busca.'
              }
            </p>
            {jobs.length === 0 && (
              <CreateButton to="/admin/vagas/nova">
                <FaPlus />
                Nova Vaga
              </CreateButton>
            )}
          </EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Vaga</Th>
                <Th>Status</Th>
                <Th>Tipo</Th>
                <Th>Candidaturas</Th>
                <Th>Criada em</Th>
                <Th style={{ width: 50 }}></Th>
              </tr>
            </thead>
            <tbody>
              {filteredJobs.map(job => (
                <tr key={job.id}>
                  <Td>
                    <JobInfo>
                      <JobTitle>{job.title}</JobTitle>
                      <JobDepartment>{job.department} • {job.location}</JobDepartment>
                    </JobInfo>
                  </Td>
                  <Td>
                    <StatusBadge $status={job.status}>
                      {getStatusLabel(job.status)}
                    </StatusBadge>
                  </Td>
                  <Td>
                    <MetaInfo>
                      <span>{getTypeLabel(job.type)}</span>
                    </MetaInfo>
                  </Td>
                  <Td>
                    <Badge $new={job.applications_count > 0}>
                      <FaUsers />
                      {job.applications_count || 0}
                    </Badge>
                  </Td>
                  <Td>
                    <MetaInfo>
                      <span>{formatDate(job.created_at)}</span>
                      <span style={{ fontSize: 12, color: '#999' }}>
                        {job.views || 0} views
                      </span>
                    </MetaInfo>
                  </Td>
                  <Td>
                    <Actions>
                      <ActionButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenu(openMenu === job.id ? null : job.id);
                        }}
                      >
                        <FaEllipsisV />
                      </ActionButton>

                      {openMenu === job.id && (
                        <ActionMenu onClick={(e) => e.stopPropagation()}>
                          <ActionItem
                            as={Link}
                            to={`/admin/vagas/editar/${job.id}`}
                          >
                            <FaEdit />
                            Editar
                          </ActionItem>

                          <ActionItem
                            onClick={() => copyShareUrl(job.slug)}
                          >
                            <FaCopy />
                            Copiar link
                          </ActionItem>

                          <ActionItem
                            as="a"
                            href={`/carreiras/${job.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FaExternalLinkAlt />
                            Ver página
                          </ActionItem>

                          {job.applications_count > 0 && (
                            <ActionItem
                              as={Link}
                              to={`/admin/vagas/${job.id}/candidaturas`}
                            >
                              <FaUsers />
                              Ver candidaturas ({job.applications_count})
                            </ActionItem>
                          )}

                          <ActionItem
                            as={Link}
                            to={`/admin/vagas/${job.id}/script`}
                          >
                            <FaFileAlt />
                            Editar Script/Manual
                          </ActionItem>

                          {job.status === 'active' && (
                            <ActionItem onClick={() => handleStatusChange(job.id, 'paused')}>
                              <FaPause />
                              Pausar vaga
                            </ActionItem>
                          )}

                          {job.status === 'paused' && (
                            <ActionItem onClick={() => handleStatusChange(job.id, 'active')}>
                              <FaPlay />
                              Reativar vaga
                            </ActionItem>
                          )}

                          {job.status !== 'closed' && (
                            <ActionItem onClick={() => handleStatusChange(job.id, 'closed')}>
                              <FaArchive />
                              Encerrar vaga
                            </ActionItem>
                          )}

                          <ActionItem
                            $danger
                            onClick={() => handleDelete(job.id)}
                          >
                            <FaTrash />
                            Excluir
                          </ActionItem>
                        </ActionMenu>
                      )}
                    </Actions>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {toast && (
        <Toast $type={toast.type}>
          {toast.message}
        </Toast>
      )}
    </Container>
  );
};

export default Jobs;
